const SELECTORS = {
    sectionWrapper: '.wp-block-ssm-section-wrapper',
    revealLayer: '.wp-block-ssm-section-wrapper__topography-mask',
    background: '.wp-block-ssm-section-wrapper__background',
};

const CLASSES = {
    expandingCard: 'is-style-expanding-card',
};

function setupExpandingCard(sectionWrapper) {
    if (!sectionWrapper.classList.contains(CLASSES.expandingCard)) {
        return null;
    }

    let rafId = null;

    const update = () => {
        rafId = null;

        const rect = sectionWrapper.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportCenter = viewportHeight * 0.5;
        const sectionCenter = rect.top + rect.height * 0.5 - 100;

        if (sectionCenter <= viewportCenter) {
            sectionWrapper.classList.add('is-animated');
        } else {
            sectionWrapper.classList.remove('is-animated');
        }
    };

    const scheduleUpdate = () => {
        if (rafId) {
            return;
        }

        rafId = requestAnimationFrame(update);
    };

    update();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
        if (rafId) {
            cancelAnimationFrame(rafId);
        }

        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', scheduleUpdate);
        sectionWrapper.classList.remove('is-animated');
    };
}

function setupTopographyReveal(sectionWrapper, revealLayer, backgroundEl) {
    let isHovering = false;
    let rafId = null;
    let lastX = 0;
    let lastY = 0;

    const updateMask = () => {
        rafId = null;

        if (!isHovering) return;

        const rect = backgroundEl.getBoundingClientRect();
        const x = lastX - rect.left;
        const y = lastY - rect.top;

        // Use CSS custom properties for better performance
        revealLayer.style.setProperty('--mask-x', `${x}px`);
        revealLayer.style.setProperty('--mask-y', `${y}px`);
    };

    const scheduleUpdate = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(updateMask);
    };

    const handleMouseEnter = () => {
        isHovering = true;
        // Use setProperty to preserve other inline styles
        revealLayer.style.setProperty('opacity', '1');
    };

    const handleMouseLeave = () => {
        isHovering = false;
        // Use setProperty to preserve other inline styles
        revealLayer.style.setProperty('opacity', '0');
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };

    const handleMouseMove = (e) => {
        lastX = e.clientX;
        lastY = e.clientY;
        scheduleUpdate();
    };

    sectionWrapper.addEventListener('mouseenter', handleMouseEnter);
    sectionWrapper.addEventListener('mouseleave', handleMouseLeave);
    sectionWrapper.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
        sectionWrapper.removeEventListener('mouseenter', handleMouseEnter);
        sectionWrapper.removeEventListener('mouseleave', handleMouseLeave);
        sectionWrapper.removeEventListener('mousemove', handleMouseMove);

        if (rafId) cancelAnimationFrame(rafId);

        // Only remove the properties we added, not the entire style attribute
        revealLayer.style.removeProperty('opacity');
        revealLayer.style.removeProperty('--mask-x');
        revealLayer.style.removeProperty('--mask-y');
    };
}

export function initSectionWrapper(sectionWrapper) {
    const revealLayer = sectionWrapper.querySelector(SELECTORS.revealLayer);
    const backgroundEl = sectionWrapper.querySelector(SELECTORS.background);

    if (!backgroundEl || sectionWrapper.sectionWrapperInitialized) {
        return null;
    }

    try {
        const eventCleanup = [];

        // Setup topography reveal if element exists
        if (revealLayer) {
            const topographyCleanup = setupTopographyReveal(sectionWrapper, revealLayer, backgroundEl);
            if (topographyCleanup) {
                eventCleanup.push(topographyCleanup);
            }
        }

        // Setup expanding card animation
        const expandingCardCleanup = setupExpandingCard(sectionWrapper, backgroundEl);
        if (expandingCardCleanup) {
            eventCleanup.push(expandingCardCleanup);
        }

        sectionWrapper.sectionWrapperInitialized = true;

        return {
            destroy() {
                eventCleanup.forEach((cleanup) => cleanup());
                delete sectionWrapper.sectionWrapperInitialized;

                // Only remove properties we added, not entire style
                if (revealLayer) {
                    revealLayer.style.removeProperty('opacity');
                    revealLayer.style.removeProperty('--mask-x');
                    revealLayer.style.removeProperty('--mask-y');
                }
            },
        };
    } catch (error) {
        console.error('Error initializing section wrapper:', error, sectionWrapper);

        return null;
    }
}

export function SectionWrapper() {
    document.querySelectorAll(SELECTORS.sectionWrapper).forEach((block) => {
        initSectionWrapper(block);
    });
}
